import React, { useState } from 'react';
import Header from '../../component/layout/Header';
import Footer from '../../component/layout/Footer';
import Page from '../../component/utility/Page';
import { custTableStyles } from '../../style/color';
import { formatNestedJson } from '../../component/utility/formatNestedJson';
import axios from 'axios';
import { mainurl } from '../../Mainurl';

import { Row, Col, Card, CardBody, Button, Input, Table } from 'reactstrap';
import {FaSearch, FaEquals, FaPlus, FaMinus, FaChartLine, FaDollarSign} from 'react-icons/fa';


const TrialBalance = () => {
    const [loading, setLoading] = useState(false);
    const [trialBalanceData, setTrialBalanceData] = useState(null);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [dateError, setDateError] = useState('');
    
    // State for collapsible groups
    const [expandedGroups, setExpandedGroups] = useState({});

    const toggleGroup = (groupKey) => {
        setExpandedGroups(prev => ({
            ...prev,
            [groupKey]: !prev[groupKey]
        }));
    };

    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined || isNaN(amount)) return '₹ 0.00';
        return `₹ ${parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    // Global function to remove duplicate groups at all levels
    const removeDuplicateGroups = (groups) => {
        if (!Array.isArray(groups)) return groups;
        
        const seenGroups = new Set();
        const uniqueGroups = [];
        
        for (const group of groups) {
            const groupKey = `${group.group_id}_${group.group_name}`;
            
            if (!seenGroups.has(groupKey)) {
                seenGroups.add(groupKey);
                
                // Recursively clean children
                if (group.children && Array.isArray(group.children)) {
                    group.children = removeDuplicateGroups(group.children);
                }
                
                uniqueGroups.push(group);
            }
        }
        
        return uniqueGroups;
    };

    // Function to collect all group_ids that have been seen to avoid duplicates globally
    const removeDuplicateGroupsGlobally = (groups) => {
        if (!groups || !Array.isArray(groups)) return [];
        
        const seenGroupIds = new Set();
        
        const processGroups = (groupArray) => {
            const uniqueGroups = [];
            
            for (const group of groupArray) {
                if (!seenGroupIds.has(group.group_id)) {
                    seenGroupIds.add(group.group_id);
                    
                    // Process children recursively
                    const processedGroup = {
                        ...group,
                        children: group.children && group.children.length > 0 ? processGroups(group.children) : []
                    };
                    
                    uniqueGroups.push(processedGroup);
                }
            }
            
            return uniqueGroups;
        };
        
        return processGroups(groups);
    };

    // Helper function to calculate total section amount including all nested groups
    const calculateSectionTotal = (groups) => {
        if (!groups || !Array.isArray(groups)) return 0;
        
        return groups.reduce((total, group) => {
            return total + calculateGroupTotalWithChildren(group);
        }, 0);
    };

    // Helper function to calculate total group amount including all children
    const calculateGroupTotalWithChildren = (group) => {
        if (!group) return 0;
        
        // Use API provided totals if available
        let total = 0;
        // For groups with explicit debit/credit totals, use the net amount
        const groupDebitTotal = parseFloat(group.group_debit_total || 0);
        const groupCreditTotal = parseFloat(group.group_credit_total || 0);
        
        if (groupDebitTotal > 0 || groupCreditTotal > 0) {
            // Use the higher total (net balance)
            total = Math.max(groupDebitTotal, groupCreditTotal);
        } else {
            // Fallback to group_total or calculate from accounts
            total = parseFloat(group.group_total || 0);
            
            if (total === 0 && group.accounts && Array.isArray(group.accounts)) {
                // Calculate from individual account balances
                total = group.accounts.reduce((sum, account) => {
                    const debitAmount = parseFloat(account.total_debits || 0);
                    const creditAmount = parseFloat(account.total_credits || 0);
                    return sum + Math.max(debitAmount, creditAmount);
                }, 0);
            }
        }
        
        // Add totals from all child groups recursively
        if (group.children && Array.isArray(group.children)) {
            group.children.forEach(childGroup => {
                total += calculateGroupTotalWithChildren(childGroup);
            });
        }
        
        return total;
    };

    // Helper function to calculate group total based on account balances
    const calculateGroupTotal = (group, balanceType) => {
        if (!group.accounts || !Array.isArray(group.accounts)) return 0;
        
        const total = group.accounts.reduce((sum, account) => {
            if (balanceType === 'debit') {
                return sum + (account.total_debits || account.debit_balance || (account.current_balance > 0 ? account.current_balance : 0));
            } else {
                return sum + (account.total_credits || account.credit_balance || (account.current_balance < 0 ? Math.abs(account.current_balance) : 0));
            }
        }, 0);
        
        // Also check children groups recursively
        if (group.children && Array.isArray(group.children)) {
            return total + group.children.reduce((sum, child) => sum + calculateGroupTotal(child, balanceType), 0);
        }
        
        return total;
    };

    // Helper function to get accounts with specific balance type
    const getAccountsWithBalance = (group, balanceType) => {
        const accounts = [];
        
        if (group.accounts && Array.isArray(group.accounts)) {
            group.accounts.forEach(account => {
                const hasDebitBalance = (account.total_debits || 0) > 0 || (account.current_balance || 0) > 0;
                const hasCreditBalance = (account.total_credits || 0) > 0 || (account.current_balance || 0) < 0;
                
                if (balanceType === 'debit' && hasDebitBalance) {
                    accounts.push(account);
                } else if (balanceType === 'credit' && hasCreditBalance) {
                    accounts.push(account);
                }
            });
        }
        
        // Also check children groups recursively
        if (group.children && Array.isArray(group.children)) {
            group.children.forEach(child => {
                accounts.push(...getAccountsWithBalance(child, balanceType));
            });
        }
        
        return accounts;
    };

    const validateDates = (from, to) => {
        if (from && to && new Date(from) > new Date(to)) {
            setDateError('To Date cannot be earlier than From Date');
            return false;
        }
        setDateError('');
        return true;
    };

    const handleFromDateChange = (e) => {
        const newFromDate = e.target.value;
        setFromDate(newFromDate);
        validateDates(newFromDate, toDate);
    };

    const handleToDateChange = (e) => {
        const newToDate = e.target.value;
        setToDate(newToDate);
        validateDates(fromDate, newToDate);
    };

    const fetchTrialBalance = () => {
        setLoading(true);
        setTrialBalanceData(null);
        
        let postData = [{
            "org_id": sessionStorage.getItem('org_id'), 
            "user_id": sessionStorage.getItem('user_id'),
            "from_date": fromDate,
            "to_date": toDate
        }];
        
        axios({
            method: 'post',
            url: `${mainurl}Accounts/tb`,
            data: postData,
            headers: { 'Content-Type': 'application/JSON', }
        })
            .then(function (response) {
                console.log('Trial Balance API Response:', response.data);
                
                if (response.data && response.data.length > 0 && response.data[0].nested_trial_balance) {
                    try {
                        // Use formatNestedJson to parse the nested JSON
                        const parsedData = formatNestedJson(response.data, false);
                        console.log('Parsed Trial Balance Data:', parsedData);
                        
                        if (parsedData[0] && parsedData[0].nested_trial_balance) {
                            const trialBalanceJson = parsedData[0].nested_trial_balance;
                            console.log('Trial Balance Structure:', trialBalanceJson);
                            
                            // Use the API structure directly - no transformation needed
                            setTrialBalanceData(trialBalanceJson);
                        } else {
                            console.error('Invalid data structure');
                            setTrialBalanceData(null);
                        }
                    } catch (error) {
                        console.error('Error parsing nested trial balance:', error);
                        setTrialBalanceData(null);
                    }
                } else {
                    // Sample data matching the actual API structure
                    console.log('No data received, using sample data');
                    setTrialBalanceData({
                        summary: {
                            is_balanced: "YES",
                            total_debits: 489000,
                            total_credits: 489000,
                            total_accounts: 24,
                            active_accounts: 8,
                            balance_difference: 0,
                            account_type_summary: [
                                {
                                    debit_total: 385000,
                                    account_type: "Asset",
                                    credit_total: 0,
                                    total_debits: 385000,
                                    account_count: 5,
                                    total_credits: 0
                                },
                                {
                                    debit_total: 104000,
                                    account_type: "Expense", 
                                    credit_total: 0,
                                    total_debits: 104000,
                                    account_count: 2,
                                    total_credits: 0
                                },
                                {
                                    debit_total: 0,
                                    account_type: "Liability",
                                    credit_total: 230000,
                                    total_debits: 0,
                                    account_count: 3,
                                    total_credits: 230000
                                },
                                {
                                    debit_total: 0,
                                    account_type: "Equity",
                                    credit_total: 200000,
                                    total_debits: 0,
                                    account_count: 1,
                                    total_credits: 200000
                                },
                                {
                                    debit_total: 0,
                                    account_type: "Revenue",
                                    credit_total: 59000,
                                    total_debits: 0,
                                    account_count: 2,
                                    total_credits: 59000
                                }
                            ]
                        },
                        structure: {
                            assets_section: {
                                section_name: "ASSETS",
                                section_debit_total: 385000,
                                section_credit_total: 0,
                                groups: [
                                    {
                                        level: 0,
                                        group_id: 1,
                                        group_name: "Assets",
                                        account_count: 5,
                                        hierarchy_path: "Assets",
                                        group_debit_total: 385000,
                                        group_description: "Resources owned by the business",
                                        group_credit_total: 0,
                                        accounts: [
                                            {
                                                account_id: 1001,
                                                account_code: "A001",
                                                account_name: "Cash in Hand",
                                                account_type: "Asset",
                                                description: "Cash on hand",
                                                total_debits: 50000,
                                                total_credits: 0,
                                                current_balance: 50000,
                                                last_transaction_date: "2025-07-08"
                                            },
                                            {
                                                account_id: 1002,
                                                account_code: "A002", 
                                                account_name: "Bank Account",
                                                account_type: "Asset",
                                                description: "Bank account balance",
                                                total_debits: 150000,
                                                total_credits: 0,
                                                current_balance: 150000,
                                                last_transaction_date: "2025-07-08"
                                            }
                                        ],
                                        children: [
                                            {
                                                level: 1,
                                                group_id: 6,
                                                group_name: "Current Assets",
                                                account_count: 2,
                                                group_debit_total: 160000,
                                                group_description: "Short-term assets",
                                                group_credit_total: 0,
                                                accounts: [
                                                    {
                                                        account_id: 1003,
                                                        account_code: "A003",
                                                        account_name: "Accounts Receivable",
                                                        account_type: "Asset",
                                                        description: "Money owed by customers",
                                                        total_debits: 75000,
                                                        total_credits: 0,
                                                        current_balance: 75000,
                                                        last_transaction_date: "2025-07-07"
                                                    },
                                                    {
                                                        account_id: 1004,
                                                        account_code: "A004",
                                                        account_name: "Inventory",
                                                        account_type: "Asset",
                                                        description: "Stock on hand",
                                                        total_debits: 85000,
                                                        total_credits: 0,
                                                        current_balance: 85000,
                                                        last_transaction_date: "2025-07-06"
                                                    }
                                                ],
                                                children: []
                                            },
                                            {
                                                level: 1,
                                                group_id: 7,
                                                group_name: "Fixed Assets",
                                                account_count: 1,
                                                group_debit_total: 25000,
                                                group_description: "Long-term assets",
                                                group_credit_total: 0,
                                                accounts: [
                                                    {
                                                        account_id: 1005,
                                                        account_code: "A005",
                                                        account_name: "Office Equipment",
                                                        account_type: "Asset",
                                                        description: "Computers and furniture",
                                                        total_debits: 25000,
                                                        total_credits: 0,
                                                        current_balance: 25000,
                                                        last_transaction_date: "2025-07-05"
                                                    }
                                                ],
                                                children: []
                                            }
                                        ]
                                    }
                                ]
                            },
                            liabilities_section: {
                                section_name: "LIABILITIES",
                                section_debit_total: 0,
                                section_credit_total: 230000,
                                groups: [
                                    {
                                        level: 0,
                                        group_id: 3,
                                        group_name: "Liabilities",
                                        account_count: 3,
                                        hierarchy_path: "Liabilities",
                                        group_debit_total: 0,
                                        group_description: "Debts and obligations",
                                        group_credit_total: 230000,
                                        accounts: [
                                            {
                                                account_id: 3001,
                                                account_code: "L001",
                                                account_name: "Accounts Payable",
                                                account_type: "Liability",
                                                description: "Money owed to suppliers",
                                                total_debits: 0,
                                                total_credits: 30000,
                                                current_balance: 30000,
                                                last_transaction_date: "2025-07-08"
                                            },
                                            {
                                                account_id: 3002,
                                                account_code: "L002",
                                                account_name: "Short Term Loan",
                                                account_type: "Liability",
                                                description: "Bank loan due within year",
                                                total_debits: 0,
                                                total_credits: 80000,
                                                current_balance: 80000,
                                                last_transaction_date: "2025-07-07"
                                            },
                                            {
                                                account_id: 3003,
                                                account_code: "L003",
                                                account_name: "Long Term Loan",
                                                account_type: "Liability",
                                                description: "Bank loan due after year",
                                                total_debits: 0,
                                                total_credits: 120000,
                                                current_balance: 120000,
                                                last_transaction_date: "2025-07-06"
                                            }
                                        ],
                                        children: []
                                    }
                                ]
                            },
                            equity_section: {
                                section_name: "EQUITY", 
                                section_debit_total: 0,
                                section_credit_total: 200000,
                                groups: [
                                    {
                                        level: 0,
                                        group_id: 4,
                                        group_name: "Equity",
                                        account_count: 1,
                                        hierarchy_path: "Equity",
                                        group_debit_total: 0,
                                        group_description: "Owner's investment",
                                        group_credit_total: 200000,
                                        accounts: [
                                            {
                                                account_id: 4001,
                                                account_code: "E001",
                                                account_name: "Capital",
                                                account_type: "Equity",
                                                description: "Owner's capital investment",
                                                total_debits: 0,
                                                total_credits: 200000,
                                                current_balance: 200000,
                                                last_transaction_date: "2025-07-01"
                                            }
                                        ],
                                        children: []
                                    }
                                ]
                            },
                            revenue_section: {
                                section_name: "REVENUE",
                                section_debit_total: 0,
                                section_credit_total: 59000,
                                groups: [
                                    {
                                        level: 0,
                                        group_id: 5,
                                        group_name: "Revenue",
                                        account_count: 2,
                                        hierarchy_path: "Revenue",
                                        group_debit_total: 0,
                                        group_description: "Income from business activities",
                                        group_credit_total: 59000,
                                        accounts: [
                                            {
                                                account_id: 5001,
                                                account_code: "R001",
                                                account_name: "Sales Revenue",
                                                account_type: "Revenue",
                                                description: "Revenue from sales",
                                                total_debits: 0,
                                                total_credits: 55000,
                                                current_balance: 55000,
                                                last_transaction_date: "2025-07-08"
                                            },
                                            {
                                                account_id: 5002,
                                                account_code: "R002",
                                                account_name: "Interest Income",
                                                account_type: "Revenue",
                                                description: "Interest earned",
                                                total_debits: 0,
                                                total_credits: 4000,
                                                current_balance: 4000,
                                                last_transaction_date: "2025-07-05"
                                            }
                                        ],
                                        children: []
                                    }
                                ]
                            },
                            expense_section: {
                                section_name: "EXPENSES",
                                section_debit_total: 104000,
                                section_credit_total: 0,
                                groups: [
                                    {
                                        level: 0,
                                        group_id: 2,
                                        group_name: "Expenses",
                                        account_count: 2,
                                        hierarchy_path: "Expenses",
                                        group_debit_total: 104000,
                                        group_description: "Costs of doing business",
                                        group_credit_total: 0,
                                        accounts: [
                                            {
                                                account_id: 2001,
                                                account_code: "E001",
                                                account_name: "Rent Expense",
                                                account_type: "Expense",
                                                description: "Office rent payments",
                                                total_debits: 24000,
                                                total_credits: 0,
                                                current_balance: 24000,
                                                last_transaction_date: "2025-07-01"
                                            },
                                            {
                                                account_id: 2002,
                                                account_code: "E002",
                                                account_name: "Salary Expense",
                                                account_type: "Expense",
                                                description: "Employee salaries",
                                                total_debits: 80000,
                                                total_credits: 0,
                                                current_balance: 80000,
                                                last_transaction_date: "2025-07-08"
                                            }
                                        ],
                                        children: []
                                    }
                                ]
                            }
                        },
                        as_of_date: toDate || new Date().toISOString().split('T')[0],
                        report_name: "Trial Balance",
                        generated_at: new Date().toISOString()
                    });
                }
                setLoading(false);
            })
            .catch(error => {
                console.error('Trial Balance API Error:', error);
                setLoading(false);
                setTrialBalanceData(null);
            });
    };

    const search_handler = () => {
        // Validate dates before making API call
        if (!fromDate || !toDate) {
            setDateError('Please select both From Date and To Date');
            return;
        }
        
        if (!validateDates(fromDate, toDate)) {
            return;
        }
        
        fetchTrialBalance();
    };

    const reset_handler = () => {
        setFromDate('');
        setToDate('');
        setTrialBalanceData(null);
        setDateError('');
        setExpandedGroups({});
    };

    // Helper to get debit and credit for a group or account
    const getDebitCredit = (item) => {
        let debit = 0, credit = 0;
        if (item.total_debits !== undefined || item.total_credits !== undefined) {
            debit = parseFloat(item.total_debits || 0);
            credit = parseFloat(item.total_credits || 0);
        } else if (item.group_debit_total !== undefined || item.group_credit_total !== undefined) {
            debit = parseFloat(item.group_debit_total || 0);
            credit = parseFloat(item.group_credit_total || 0);
        } else if (item.current_balance !== undefined) {
            if (item.current_balance >= 0) debit = item.current_balance;
            else credit = Math.abs(item.current_balance);
        }
        return { debit, credit };
    };

    // Function to render group rows in table format with expand/collapse
    const renderGroupRows = (groups, level = 0, parentKey = '') => {
        if (!groups || !Array.isArray(groups)) return [];
        
        const rows = [];
        
        groups.forEach((group, index) => {
            const groupKey = parentKey ? `${parentKey}_${group.group_id}` : `${group.group_id}`;
            const isExpanded = expandedGroups[groupKey];
            const hasChildren = group.children && group.children.length > 0;
            const hasAccounts = group.accounts && group.accounts.length > 0;
            const canExpand = hasChildren || hasAccounts;
            
            // Get debit and credit amounts for the group
            const { debit, credit } = getDebitCredit(group);
            
            // Group row
            rows.push(
                <tr key={groupKey} style={{ backgroundColor: level === 0 ? '#f8f9fa' : 'transparent' }}>
                    <td style={{ paddingLeft: `${level * 20 + 10}px` }}>
                        <div className="d-flex align-items-center">
                            {canExpand && (
                                <span
                                    style={{ cursor: 'pointer', marginRight: '8px' }}
                                    onClick={() => toggleGroup(groupKey)}
                                >
                                    {isExpanded ? 
                                        <FaMinus className="text-danger" size={12} /> : 
                                        <FaPlus className="text-success" size={12} />
                                    }
                                </span>
                            )}
                            <strong style={{ fontSize: level === 0 ? '1em' : '0.9em' }}>
                                {group.group_name}
                            </strong>
                        </div>
                    </td>
                    <td style={{ textAlign: 'right', color: debit > 0 ? '#198754' : '#6c757d' }}>
                        {debit > 0 ? formatCurrency(debit) : '-'}
                    </td>
                    <td style={{ textAlign: 'right', color: credit > 0 ? '#dc3545' : '#6c757d' }}>
                        {credit > 0 ? formatCurrency(credit) : '-'}
                    </td>
                </tr>
            );
            
            // Render accounts if expanded
            if (isExpanded && hasAccounts) {
                group.accounts.forEach((account) => {
                    const accountKey = `${groupKey}_acc_${account.account_id}`;
                    const { debit: accDebit, credit: accCredit } = getDebitCredit(account);
                    
                    rows.push(
                        <tr key={accountKey}>
                            <td style={{ paddingLeft: `${(level + 1) * 20 + 10}px` }}>
                                <div className="d-flex align-items-center">
                                    <span style={{ marginRight: '8px', width: '16px' }}></span>
                                    <span style={{ fontSize: '0.85em' }}>
                                        {account.account_code} - {account.account_name}
                                    </span>
                                </div>
                            </td>
                            <td style={{ textAlign: 'right', color: accDebit > 0 ? '#198754' : '#6c757d' }}>
                                {accDebit > 0 ? formatCurrency(accDebit) : '-'}
                            </td>
                            <td style={{ textAlign: 'right', color: accCredit > 0 ? '#dc3545' : '#6c757d' }}>
                                {accCredit > 0 ? formatCurrency(accCredit) : '-'}
                            </td>
                        </tr>
                    );
                });
            }
            
            // Render children groups if expanded
            if (isExpanded && hasChildren) {
                const childRows = renderGroupRows(group.children, level + 1, groupKey);
                rows.push(...childRows);
            }
        });
        
        return rows;
    };

    // Calculate totals from the API data
    const getTotals = () => {
        if (!trialBalanceData || !trialBalanceData.structure) {
            return { totalDebit: 0, totalCredit: 0, difference: 0, isBalanced: true, apiTotalDebit: 0, apiTotalCredit: 0 };
        }

        // Get totals from API summary if available
        const summary = trialBalanceData.summary;
        const apiTotalDebit = parseFloat(summary?.total_debits || 0);
        const apiTotalCredit = parseFloat(summary?.total_credits || 0);

        // Calculate totals from sections
        const structure = trialBalanceData.structure;
        let calculatedDebitTotal = 0;
        let calculatedCreditTotal = 0;

        const sections = [
            structure.assets_section,
            structure.liabilities_section,
            structure.equity_section,
            structure.revenue_section,
            structure.expense_section
        ];

        sections.forEach(section => {
            if (section) {
                calculatedDebitTotal += parseFloat(section.section_debit_total || 0);
                calculatedCreditTotal += parseFloat(section.section_credit_total || 0);
            }
        });

        const difference = Math.abs(calculatedDebitTotal - calculatedCreditTotal);
        
        return {
            totalDebit: apiTotalDebit || calculatedDebitTotal,
            totalCredit: apiTotalCredit || calculatedCreditTotal,
            difference: parseFloat(summary?.balance_difference || difference),
            isBalanced: summary?.is_balanced === 'YES' || difference < 0.01,
            apiTotalDebit: apiTotalDebit,
            apiTotalCredit: apiTotalCredit
        };
    };

    const { totalDebit, totalCredit, difference, isBalanced, apiTotalDebit, apiTotalCredit } = getTotals();

    return (
        <>
            <Header />
            <Page title='Trial Balance' breadcrumbs='Home / Financial / Trial Balance'>
                <Row className='p-3'>
                    <Col xs={12}>
                        <Card className='mb-3' color="primary" outline>
                            <CardBody>
                                <h6 className='mb-3'>
                                    <FaChartLine className='me-2' />
                                    Search Criteria
                                </h6>
                                <Row>
                                    <Col md={4}>
                                        <label className='form-label'>From Date</label>
                                        <Input 
                                            type='date' 
                                            value={fromDate} 
                                            onChange={handleFromDateChange}
                                            max={toDate || undefined}
                                            className={dateError ? 'is-invalid' : ''}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <label className='form-label'>To Date</label>
                                        <Input 
                                            type='date' 
                                            value={toDate} 
                                            onChange={handleToDateChange}
                                            min={fromDate || undefined}
                                            className={dateError ? 'is-invalid' : ''}
                                        />
                                    </Col>
                                    <Col md={4} className='d-flex align-items-end'>
                                        <Button 
                                            className='text-white me-2' 
                                            color='primary' 
                                            size="sm" 
                                            onClick={search_handler}
                                            disabled={!!dateError || !fromDate || !toDate || loading}
                                        > 
                                            <FaSearch className='me-1' size={16} /> Generate Report
                                        </Button>
                                        <Button className='text-white' color='secondary' size="sm" onClick={reset_handler}> 
                                            Reset
                                        </Button>
                                    </Col>
                                </Row>
                                {dateError && (
                                    <Row className='mt-2'>
                                        <Col xs={12}>
                                            <div className='text-danger small'>
                                                <i className="fa fa-exclamation-triangle me-1"></i>
                                                {dateError}
                                            </div>
                                        </Col>
                                    </Row>
                                )}
                            </CardBody>
                        </Card>

                        {/* Trial Balance Content */}
                        {trialBalanceData ? (
                            <>
                                {/* Single Table Layout - Hierarchical Trial Balance */}
                                <Card className='shadow-sm border-0'>
                                    <div className='card-header bg-white border-bottom py-3'>
                                        <div className="d-flex justify-content-between align-items-center">
                                            <h4 className='mb-0 text-dark fw-bold' style={{ fontSize: '1.5em', letterSpacing: '1px' }}>
                                                <FaEquals className='me-2' />
                                                TRIAL BALANCE
                                            </h4>
                                            <div className="d-flex gap-4">
                                                <div className="text-center">
                                                    <span className='fw-bold text-danger' style={{ fontSize: '1.1em' }}>
                                                        Total Dr: {formatCurrency(totalDebit)}
                                                    </span>
                                                    <br />
                                                    <small className="text-muted" style={{ fontSize: '0.7em' }}>
                                                        API: {apiTotalDebit}
                                                    </small>
                                                </div>
                                                <div className="text-center">
                                                    <span className='fw-bold text-success' style={{ fontSize: '1.1em' }}>
                                                        Total Cr: {formatCurrency(totalCredit)}
                                                    </span>
                                                    <br />
                                                    <small className="text-muted" style={{ fontSize: '0.7em' }}>
                                                        API: {apiTotalCredit}
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <CardBody className='p-0'>
                                        <div className='table-responsive'>
                                            <table className='table table-bordered table-sm'>
                                                <thead style={{ backgroundColor: '#f8f9fa' }}>
                                                    <tr>
                                                        <th>Account</th>
                                                        <th style={{ textAlign: 'right' }}>Debit</th>
                                                        <th style={{ textAlign: 'right' }}>Credit</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {/* Render all sections: assets, liabilities, equity, revenue, expenses */}
                                                    {trialBalanceData && trialBalanceData.structure && (
                                                        <>
                                                            {/* ASSETS */}
                                                            {trialBalanceData.structure.assets_section && (
                                                                <>
                                                                    <tr style={{ backgroundColor: '#e2f0d9' }}>
                                                                        <td colSpan={3}><strong>{trialBalanceData.structure.assets_section.section_name}</strong></td>
                                                                    </tr>
                                                                    {renderGroupRows(trialBalanceData.structure.assets_section.groups)}
                                                                </>
                                                            )}
                                                            {/* LIABILITIES */}
                                                            {trialBalanceData.structure.liabilities_section && (
                                                                <>
                                                                    <tr style={{ backgroundColor: '#fbeee6' }}>
                                                                        <td colSpan={3}><strong>{trialBalanceData.structure.liabilities_section.section_name}</strong></td>
                                                                    </tr>
                                                                    {renderGroupRows(trialBalanceData.structure.liabilities_section.groups)}
                                                                </>
                                                            )}
                                                            {/* EQUITY */}
                                                            {trialBalanceData.structure.equity_section && (
                                                                <>
                                                                    <tr style={{ backgroundColor: '#e6f0fa' }}>
                                                                        <td colSpan={3}><strong>{trialBalanceData.structure.equity_section.section_name}</strong></td>
                                                                    </tr>
                                                                    {renderGroupRows(trialBalanceData.structure.equity_section.groups)}
                                                                </>
                                                            )}
                                                            {/* REVENUE */}
                                                            {trialBalanceData.structure.revenue_section && (
                                                                <>
                                                                    <tr style={{ backgroundColor: '#e6f7fa' }}>
                                                                        <td colSpan={3}><strong>{trialBalanceData.structure.revenue_section.section_name}</strong></td>
                                                                    </tr>
                                                                    {renderGroupRows(trialBalanceData.structure.revenue_section.groups)}
                                                                </>
                                                            )}
                                                            {/* EXPENSES */}
                                                            {trialBalanceData.structure.expense_section && (
                                                                <>
                                                                    <tr style={{ backgroundColor: '#fbeee6' }}>
                                                                        <td colSpan={3}><strong>{trialBalanceData.structure.expense_section.section_name}</strong></td>
                                                                    </tr>
                                                                    {renderGroupRows(trialBalanceData.structure.expense_section.groups)}
                                                                </>
                                                            )}
                                                        </>
                                                    )}
                                                </tbody>
                                                {/* Totals row */}
                                                {trialBalanceData && trialBalanceData.summary && (
                                                    <tfoot>
                                                        <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                                                            <td>Total</td>
                                                            <td style={{ textAlign: 'right', color: '#198754' }}>{formatCurrency(trialBalanceData.summary.total_debits)}</td>
                                                            <td style={{ textAlign: 'right', color: '#dc3545' }}>{formatCurrency(trialBalanceData.summary.total_credits)}</td>
                                                        </tr>
                                                    </tfoot>
                                                )}
                                            </table>
                                        </div>
                                    </CardBody>
                                </Card>
                            </>
                        ) : (
                            <Card className='mb-3 shadow-sm border-0'>
                                <CardBody className="text-center py-5">
                                    {loading ? (
                                        <>
                                            <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                                            <h5 className="text-muted">Loading Trial Balance...</h5>
                                        </>
                                    ) : (
                                        <>
                                            <FaEquals size={48} className="text-muted mb-3" />
                                            <h5 className="text-muted">No Trial Balance Data Available</h5>
                                            <p className="text-muted">
                                                Please select a date range and click "Generate Report" to view the trial balance.
                                            </p>
                                        </>
                                    )}
                                </CardBody>
                            </Card>
                        )}

                        {/* Trial Balance Summary - Bottom Section */}
                        {trialBalanceData && (
                            <Card className='mt-4 shadow-sm border-0'>
                                <CardBody className='p-4'>
                                    <h5 className='mb-4 text-dark fw-bold'>
                                        <FaEquals className='me-2' />
                                        Trial Balance Summary
                                        <small className="ms-2 text-muted">
                                            {trialBalanceData.as_of_date ? 
                                                `(As of: ${new Date(trialBalanceData.as_of_date).toLocaleDateString()})` :
                                                trialBalanceData.generated_at ? 
                                                    `(Generated: ${new Date(trialBalanceData.generated_at).toLocaleString()})` : 
                                                    '(Current Period)'
                                            }
                                        </small>
                                    </h5>
                                    <Row className='g-3'>
                                        <Col md={3}>
                                            <div className='p-3 bg-light rounded'>
                                                <h6 className='text-danger fw-bold'>Total Debit</h6>
                                                <h4 className='text-danger mb-0'>
                                                    {formatCurrency(totalDebit)}
                                                </h4>
                                                <small className="text-muted">
                                                    ({trialBalanceData.summary?.active_accounts || 0} accounts)
                                                </small>
                                                <br />
                                                <small className="text-muted" style={{ fontSize: '0.7em' }}>
                                                    API: {apiTotalDebit}
                                                </small>
                                            </div>
                                        </Col>
                                        <Col md={3}>
                                            <div className='p-3 bg-light rounded'>
                                                <h6 className='text-success fw-bold'>Total Credit</h6>
                                                <h4 className='text-success mb-0'>
                                                    {formatCurrency(totalCredit)}
                                                </h4>
                                                <small className="text-muted">
                                                    ({trialBalanceData.summary?.active_accounts || 0} accounts)
                                                </small>
                                                <br />
                                                <small className="text-muted" style={{ fontSize: '0.7em' }}>
                                                    API: {apiTotalCredit}
                                                </small>
                                            </div>
                                        </Col>
                                        <Col md={3}>
                                            <div className={`p-3 rounded ${isBalanced ? 'bg-success text-white' : 'bg-danger text-white'}`}>
                                                <h6 className="fw-bold">Balance Status</h6>
                                                <h4 className="mb-0">
                                                    {isBalanced ? 'Balanced ✓' : 'Unbalanced ✗'}
                                                </h4>
                                                {!isBalanced && (
                                                    <>
                                                        <small>Difference: {formatCurrency(difference)}</small>
                                                        <br />
                                                        <small style={{ fontSize: '0.7em' }}>
                                                            API Diff: {trialBalanceData.summary?.balance_difference || difference}
                                                        </small>
                                                    </>
                                                )}
                                            </div>
                                        </Col>
                                        <Col md={3}>
                                            <div className='p-3 bg-light rounded'>
                                                <h6 className='text-info fw-bold'>Total Accounts</h6>
                                                <h4 className='text-info mb-0'>
                                                    {trialBalanceData.summary?.total_accounts || 0}
                                                </h4>
                                                <small className="text-muted">
                                                    {trialBalanceData.summary?.active_accounts ? 
                                                        `${trialBalanceData.summary.active_accounts} active` : 
                                                        'All accounts'
                                                    }
                                                </small>
                                            </div>
                                        </Col>
                                    </Row>
                                    
                                    {/* Account Type Summary */}
                                    {trialBalanceData.summary?.account_type_summary && (
                                        <>
                                            <h6 className='mb-3 text-dark fw-bold mt-4'>
                                                Account Type Breakdown
                                            </h6>
                                            <Row className='g-2'>
                                                {trialBalanceData.summary.account_type_summary.map((typeData, index) => (
                                                    <Col md={6} lg={4} key={index}>
                                                        <div className='p-3 bg-white rounded border'>
                                                            <strong className="text-dark">{typeData.account_type}</strong>
                                                            <div className="d-flex justify-content-between mt-1">
                                                                <small className="text-danger">Dr: {formatCurrency(typeData.debit_total)}</small>
                                                                <small className="text-success">Cr: {formatCurrency(typeData.credit_total)}</small>
                                                            </div>
                                                            <small className="text-muted">{typeData.account_count} accounts</small>
                                                            <br />
                                                            <small className="text-muted" style={{ fontSize: '0.6em' }}>
                                                                API: Dr {typeData.debit_total} | Cr {typeData.credit_total}
                                                            </small>
                                                        </div>
                                                    </Col>
                                                ))}
                                            </Row>
                                        </>
                                    )}
                                </CardBody>
                            </Card>
                        )}

                        {/* No Data Message */}
                        {!trialBalanceData && !loading && (
                            <Card className='mb-3 shadow-sm border-0'>
                                <CardBody className="text-center py-5">
                                    <FaEquals size={48} className="text-muted mb-3" />
                                    <h5 className="text-muted">No Trial Balance Data Available</h5>
                                    <p className="text-muted">
                                        Please select a date range and click "Generate Report" to view the trial balance.
                                    </p>
                                </CardBody>
                            </Card>
                        )}
                    </Col>
                </Row>
            </Page>
            <Footer />
        </>
    );
};

export default TrialBalance;
